import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageSquareReply, Send } from "lucide-react";

const when = (d) => new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function ReplyBox({ updateId, parentId, onSent, testid }) {
  const [text, setText] = useState("");
  async function send(e) {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post(`/daily-updates/${updateId}/replies`, { body: text, parent_id: parentId }); setText(""); onSent(); }
    catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <form onSubmit={send} className="flex gap-2 mt-2">
      <Input data-testid={`${testid}-input`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply" className="h-9" />
      <Button data-testid={`${testid}-send`} type="submit" size="sm" className="btn-brand h-9"><Send size={14} /></Button>
    </form>
  );
}

export function UpdateReplies({ update }) {
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const canReply = isParent || ["master_admin", "super_admin", "principal", "teacher"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);

  const load = () => api.get(`/daily-updates/${update.id}/replies`).then((r) => setRows(r.data)).catch(() => setRows([]));
  function toggle() { if (!open && rows === null) load(); setOpen(!open); }

  const threads = (rows || []).reduce((acc, r) => { (acc[r.thread_parent_id] ||= []).push(r); return acc; }, {});

  return (
    <div className="mt-3">
      <button data-testid={`replies-toggle-${update.id}`} onClick={toggle} className="text-xs font-semibold flex items-center gap-1" style={{ color: "var(--brand)" }}>
        <MessageSquareReply size={14} /> {open ? "Hide replies" : isParent ? "Reply / view my replies" : "Parent replies"}
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          {rows !== null && Object.keys(threads).length === 0 && <p className="text-xs text-slate-500">No replies yet.</p>}
          {Object.entries(threads).map(([pid, list]) => (
            <div key={pid} data-testid={`reply-thread-${pid}`} className="rounded-xl bg-slate-50 p-3 space-y-1.5">
              {!isParent && <div className="text-[11px] font-semibold text-slate-500">Thread with {list.find((x) => x.from_role === "parent")?.from_name || "parent"}</div>}
              {list.map((r) => (
                <div key={r.id} className="text-sm">
                  <span className="font-semibold text-slate-800">{r.from_id === user.id ? "You" : r.from_name}</span>
                  <span className="text-[11px] text-slate-400"> • {ROLE_LABELS[r.from_role]} • {when(r.created_at)}</span>
                  <div className="text-slate-700 whitespace-pre-line">{r.body}</div>
                </div>
              ))}
              {!isParent && canReply && <ReplyBox updateId={update.id} parentId={pid} onSent={load} testid={`staff-reply-${pid}`} />}
            </div>
          ))}
          {isParent && <ReplyBox updateId={update.id} onSent={load} testid={`parent-reply-${update.id}`} />}
        </div>
      )}
    </div>
  );
}
